import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ECampaignStatus, TCampaignError } from '~/features/campaigns/models'
import { queryKeys } from '~/features/campaigns/utils'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'

type ArchiveCampaignResponse = {
  message: string
}

type TArchiveCampaignPayload = {
  id: string
  agentId: string
}

const archiveCampaign = async (
  campaigns: TArchiveCampaignPayload[],
  restore = false,
): Promise<ArchiveCampaignResponse> => {
  const response = await HttpService.post<ArchiveCampaignResponse>(
    {
      path: EServerPath.campaignsArchive,
      payload: {
        campaigns,
        ...(restore && { status: ECampaignStatus.draft }),
      },
    },
    EHttpService.internal,
  )
  if (!response.data) {
    throw new Error('Failed to delete campaign')
  }
  return response.data
}

const useArchiveCampaigns = (agentId: string, restore = false) => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    ArchiveCampaignResponse,
    TCampaignError,
    TArchiveCampaignPayload[]
  >({
    mutationFn: (campaigns) => archiveCampaign(campaigns, restore),
    onSuccess: (_, campaigns) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.campaignsBase(agentId),
      })

      if (campaigns.length === 1) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.campaign(campaigns[0].id),
        })
      }
    },
  })

  return mutation
}

export default useArchiveCampaigns
